import { httpClient } from './httpClient';

export interface Memo {
  id: number;
  userId: number;
  title: string;
  content: string;
  color: string;
  positionX: number;
  positionY: number;
  createdAt: string;
  updatedAt: string;
  type?: 'note' | 'checklist'; // Type of memo
  // Google Keep features (optional until migration is applied)
  isPinned?: boolean;
  isArchived?: boolean;
  labels?: string[];
  reminder?: string;
  checklistItems?: ChecklistItem[];
  images?: string[];
  // Sharing fields
  accessLevel?: 'owner' | 'view' | 'edit';
  ownerId?: number;
  ownerName?: string;
  ownerEmail?: string;
}

export interface MemoShare {
  userId: number;
  userName: string;
  userEmail: string;
  accessLevel: 'view' | 'edit';
  sharedAt: string;
}

export interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
}

export interface CreateMemoData {
  title: string;
  content: string;
  color?: string;
  positionX?: number;
  positionY?: number;
  type?: 'note' | 'checklist';
  // Google Keep features
  isPinned?: boolean;
  isArchived?: boolean;
  labels?: string[];
  reminder?: string;
  checklistItems?: ChecklistItem[];
  images?: string[];
}

export interface UpdateMemoData {
  title?: string;
  content?: string;
  color?: string;
  positionX?: number;
  positionY?: number;
  type?: 'note' | 'checklist';
  // Google Keep features
  isPinned?: boolean;
  isArchived?: boolean;
  labels?: string[];
  reminder?: string;
  checklistItems?: ChecklistItem[];
  images?: string[];
}

export interface ShareMemoData {
  userId: number;
  accessLevel: 'view' | 'edit';
}

const memoService = {
  // Get all memos for the current user
  getUserMemos: async (): Promise<Memo[]> => {
    return httpClient.get<Memo[]>('/api/memos');
  },

  // Get a specific memo by ID
  getMemoById: async (id: number): Promise<Memo> => {
    return httpClient.get<Memo>(`/api/memos/${id}`);
  },

  //Create a new memo
  createMemo: async (memoData: CreateMemoData): Promise<Memo> => {
    return httpClient.post<Memo>('/api/memos', memoData);
  },

  // Update an existing memo
  updateMemo: async (id: number, memoData: UpdateMemoData): Promise<Memo> => {
    return httpClient.put<Memo>(`/api/memos/${id}`, memoData);
  },

  // Delete a memo
  deleteMemo: async (id: number): Promise<void> => {
    await httpClient.delete(`/api/memos/${id}`);
  },

  // Sharing methods
  shareMemo: async (memoId: number, shares: ShareMemoData[]): Promise<MemoShare[]> => {
    return httpClient.post<MemoShare[]>(`/api/memos/${memoId}/share`, { shares });
  },

  removeMemoShare: async (memoId: number, userId: number): Promise<void> => {
    await httpClient.delete(`/api/memos/${memoId}/share/${userId}`);
  },

  getMemoShares: async (memoId: number): Promise<MemoShare[]> => {
    return httpClient.get<MemoShare[]>(`/api/memos/${memoId}/shares`);
  },

  updateMemoShareAccess: async (memoId: number, userId: number, accessLevel: 'view' | 'edit'): Promise<MemoShare> => {
    return httpClient.patch<MemoShare>(`/api/memos/${memoId}/share/${userId}`, { accessLevel });
  }
};

export default memoService;

